import { useState, useCallback } from 'react';
import { axdAPI } from '../services/api';

/**
 * Estado y operaciones del módulo AXD (2026-05).
 * Cada herramienta tiene un VALOR y un MODO de cobro:
 *  - 'dividido': el valor se reparte en partes iguales entre los proyectos
 *    marcados (marcar 1 = costo completo; marcar 5 = 20% c/u).
 *  - 'unitario': cada proyecto marcado paga el valor completo (total = valor × Nº).
 * El reparto es calculado a partir de cuántos proyectos están marcados.
 * - proyectos: tabla `clients` (compartida con el módulo Cloud)
 * - apps: catálogo `axd_apps`, cada una con su valor (`costo`) y `modo`
 * - asignaciones: relación N:N `axd_prorrateo`, indexada { appId: { clientId: true } }
 */
export const useAxd = (showNotification) => {
  const [proyectos, setProyectos] = useState([]);
  const [apps, setApps] = useState([]);
  const [asignaciones, setAsignaciones] = useState({});

  const loadProyectos = useCallback(async () => {
    try {
      const res = await axdAPI.getProyectos();
      setProyectos(res.data.proyectos || []);
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudieron cargar los proyectos');
    }
  }, [showNotification]);

  const loadApps = useCallback(async () => {
    try {
      const res = await axdAPI.getApps();
      setApps((res.data.apps || []).map((a) => ({
        ...a,
        costo: parseFloat(a.costo) || 0,
        modo: a.modo === 'dividido' ? 'dividido' : 'unitario'
      })));
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo cargar el catálogo de herramientas');
    }
  }, [showNotification]);

  const loadAsignaciones = useCallback(async () => {
    try {
      const res = await axdAPI.getAsignaciones();
      const map = {};
      for (const r of res.data.prorrateo || []) {
        if (!map[r.app_id]) map[r.app_id] = {};
        map[r.app_id][r.client_id] = true;
      }
      setAsignaciones(map);
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudieron cargar las asignaciones');
    }
  }, [showNotification]);

  const addProyecto = useCallback(async (name) => {
    try {
      await axdAPI.createProyecto(name);
      const res = await axdAPI.getProyectos();
      setProyectos(res.data.proyectos || []);
      showNotification?.('success', 'Proyecto creado', `"${name}" se agregó correctamente`);
      return true;
    } catch (error) {
      showNotification?.('error', 'Error', error.response?.data?.error || 'No se pudo crear el proyecto');
      return false;
    }
  }, [showNotification]);

  const removeProyecto = useCallback(async (proyecto) => {
    try {
      await axdAPI.deleteProyecto(proyecto.id);
      setProyectos((prev) => prev.filter((p) => p.id !== proyecto.id));
      showNotification?.('success', 'Proyecto eliminado', `"${proyecto.name}" fue eliminado`);
      return true;
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo eliminar el proyecto');
      return false;
    }
  }, [showNotification]);

  const reorderProyectos = useCallback(async (newList) => {
    setProyectos(newList);
    try {
      await axdAPI.reordenarProyectos(newList.map((p) => p.id));
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar el nuevo orden');
    }
  }, [showNotification]);

  const addApp = useCallback(async (name, costo, modo) => {
    try {
      await axdAPI.createApp(name, costo, modo);
      const res = await axdAPI.getApps();
      setApps((res.data.apps || []).map((a) => ({
        ...a,
        costo: parseFloat(a.costo) || 0,
        modo: a.modo === 'dividido' ? 'dividido' : 'unitario'
      })));
      showNotification?.('success', 'Herramienta creada', `"${name}" se agregó al catálogo`);
      return true;
    } catch (error) {
      showNotification?.('error', 'Error', error.response?.data?.error || 'No se pudo crear la herramienta');
      return false;
    }
  }, [showNotification]);

  const updateAppCosto = useCallback(async (app, costo) => {
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, costo } : a)));
    try {
      await axdAPI.updateApp(app.id, { costo });
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar el costo');
    }
  }, [showNotification]);

  const updateAppName = useCallback(async (app, name) => {
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, name } : a)));
    try {
      await axdAPI.updateApp(app.id, { name });
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar el nombre');
    }
  }, [showNotification]);

  const updateAppModo = useCallback(async (app, modo) => {
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, modo } : a)));
    try {
      await axdAPI.updateApp(app.id, { modo });
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar el modo de cobro');
    }
  }, [showNotification]);

  const removeApp = useCallback(async (app) => {
    try {
      await axdAPI.deleteApp(app.id);
      setApps((prev) => prev.filter((a) => a.id !== app.id));
      setAsignaciones((prev) => {
        const next = { ...prev };
        delete next[app.id];
        return next;
      });
      showNotification?.('success', 'Herramienta eliminada', `"${app.name}" se eliminó`);
      return true;
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo eliminar la herramienta');
      return false;
    }
  }, [showNotification]);

  // Reordena el catálogo de apps (arrastrar para reordenar) y lo persiste.
  const reorderApps = useCallback(async (newList) => {
    setApps(newList);
    try {
      await axdAPI.reordenarApps(newList.map((a) => a.id));
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar el orden');
    }
  }, [showNotification]);

  // Asigna o quita una app de un proyecto (toggle).
  const setAsignacion = useCallback(async (appId, clientId, asignado) => {
    setAsignaciones((prev) => {
      const next = { ...prev };
      const row = { ...(next[appId] || {}) };
      if (asignado) row[clientId] = true;
      else delete row[clientId];
      next[appId] = row;
      return next;
    });
    try {
      await axdAPI.setAsignacion({ appId, clientId, asignado });
    } catch (error) {
      showNotification?.('error', 'Error', 'No se pudo guardar la asignación');
    }
  }, [showNotification]);

  return {
    proyectos,
    apps,
    asignaciones,
    loadProyectos,
    loadApps,
    loadAsignaciones,
    addProyecto,
    removeProyecto,
    reorderProyectos,
    addApp,
    updateAppCosto,
    updateAppName,
    updateAppModo,
    removeApp,
    reorderApps,
    setAsignacion
  };
};
